import { useTranslation } from "react-i18next";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import { usePricingActions } from "@/features/pricing/hooks/use-pricing-actions";
import { HarmonyDiamond } from "../icons";
import { cn } from "@/lib/utils";
import { headerActionClass } from "@/components/layout/header-action";

const MIN_VISIBLE_PROGRESS = 4;

const CREDIT_GRADIENT_STOPS = {
  accent: [
    "oklch(0.8 0.14 var(--module-hue))",
    "var(--module-accent)",
    "oklch(0.64 0.17 calc(var(--module-hue) + 48))",
    "oklch(0.7 0.15 calc(var(--module-hue) + 96))",
  ],
  warning: [
    "var(--harmony-warning-from)",
    "var(--harmony-warning-via)",
    "var(--harmony-warning-to)",
    "var(--harmony-warning-end)",
  ],
  critical: [
    "var(--harmony-critical-from)",
    "var(--harmony-critical-via)",
    "var(--harmony-critical-to)",
    "var(--harmony-critical-end)",
  ],
} as const;

const CREDIT_FILL_CLASS = {
  accent: "harmony-accent-gradient",
  warning: "harmony-warning-gradient",
  critical: "harmony-critical-gradient",
} as const;

/** Donut da bar ile aynı 4 duraklı gradyanı conic olarak çizer. */
function creditDonutStyle(
  variant: keyof typeof CREDIT_GRADIENT_STOPS,
  progress: number,
) {
  const [from, via, to, end] = CREDIT_GRADIENT_STOPS[variant];
  return {
    background: `conic-gradient(${from} 0%, ${via} ${progress * 0.32}%, ${to} ${progress * 0.64}%, ${end} ${progress}%, rgba(156, 163, 175, 0.35) ${progress}% 100%)`,
  };
}

export const HeaderCreditsIndicator: React.FC = () => {
  const { t } = useTranslation();
  const { data: billingData, isLoading } = useBillingHistory();
  const { openPricingModalWithTopup } = usePricingActions();

  const usage = billingData?.data?.usage;

  if (!isLoading && !usage) {
    return null;
  }

  const remainingSubscription = usage?.tokens.subscription || 0;
  const remainingOneTime = usage?.tokens.oneTime || 0;
  const maxSubscription = usage?.maxTokens.subscription || 0;
  const maxOneTime = usage?.maxTokens.oneTime || 0;

  const totalRemaining = remainingSubscription + remainingOneTime;
  const totalMax = maxSubscription + maxOneTime;
  const percentage =
    totalMax > 0
      ? Math.min(Math.round((totalRemaining / totalMax) * 100), 100)
      : 0;

  const creditVariant =
    percentage <= 10 ? "critical" : percentage <= 30 ? "warning" : "accent";
  const donutProgress = Math.max(percentage, MIN_VISIBLE_PROGRESS);
  const donutStyle = creditDonutStyle(creditVariant, donutProgress);
  const buttonTitle = `${t("credits.remaining")}: ${totalRemaining.toLocaleString()} / ${totalMax.toLocaleString()} (${percentage}%)`;
  const formattedRemaining = totalRemaining.toLocaleString();
  const formattedMax = totalMax.toLocaleString();
  const activePlanName = billingData?.data?.pricingPlan?.name || "Free";
  const activePlanLabel = activePlanName.trim().split(/\s+/)[0] || "Free";

  return (
    <div className="group h-8 w-8 shrink-0 overflow-visible transition-[width] duration-300 ease-out hover:w-[280px] focus-within:w-[280px]">
      <button
        type="button"
        onClick={() => openPricingModalWithTopup()}
        className={cn(
          headerActionClass,
          "w-full overflow-hidden px-1 hover:justify-start hover:px-2 focus-visible:justify-start focus-visible:px-2",
        )}
        aria-label={buttonTitle}
      >
        {isLoading ? (
          <span className="flex size-5 animate-pulse items-center justify-center">
            <span className="size-5 rounded-full bg-gray-200 dark:bg-gray-700" />
          </span>
        ) : (
          <span className="flex h-full w-full min-w-0 flex-nowrap items-center justify-center gap-0 whitespace-nowrap group-hover:justify-start group-hover:gap-2 group-focus-within:justify-start group-focus-within:gap-2">
            <span className="relative flex size-5 shrink-0 items-center justify-center">
              <span
                className="absolute inset-0 rounded-full"
                style={donutStyle}
              />
              <span className="absolute inset-[4px] rounded-full bg-bg-primary dark:bg-gray-900 group-hover:bg-bg-secondary dark:group-hover:bg-gray-800" />
              <HarmonyDiamond
                size={8}
                className="relative z-10 text-text-primary dark:text-gray-100"
              />
            </span>

            <span className="max-w-0 overflow-hidden text-[11px] font-semibold text-text-primary opacity-0 transition-all duration-300 ease-out group-hover:max-w-[58px] group-hover:opacity-100 group-focus-within:max-w-[58px] group-focus-within:opacity-100 dark:text-gray-100">
              {activePlanLabel}
            </span>

            <span className="min-w-0 max-w-0 overflow-hidden opacity-0 transition-all duration-300 ease-out group-hover:max-w-[188px] group-hover:opacity-100 group-focus-within:max-w-[188px] group-focus-within:opacity-100">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-[10px] font-semibold text-text-primary dark:text-gray-100">
                  {t("credits.remaining")}
                </span>
                <span className="h-2 w-[84px] overflow-hidden rounded-full bg-gray-200/80 dark:bg-gray-700/80">
                  <span
                    className={`block h-full rounded-full transition-all duration-300 ${CREDIT_FILL_CLASS[creditVariant]}`}
                    style={{ width: `${donutProgress}%` }}
                  />
                </span>
                <span className="text-[10px] font-medium text-text-secondary dark:text-gray-300">
                  {formattedRemaining} / {formattedMax}
                </span>
              </span>
            </span>
          </span>
        )}
      </button>
    </div>
  );
};
